/**
 * Pending tab — everything Jess has asked Merlin to watch or come back on
 * (kanban 43160d8b). Jess (2026-10-01): "Where would it prompt that, say if
 * I asked it to do something next Monday? Where would that show up?" Scott:
 * "a pending bucket of things that I'm expecting Merlin to give me answers
 * on in the future."
 *
 * One row per follow-up Merlin committed to (services/merlinFollowups.ts):
 * what it will check, the item it hangs on, who asked, when it is due and
 * where it stands. Grouped: answers not yet read on top, then what is due
 * this week, then later, then what is settled. Cancel asks once, under the
 * row, with a reason (recorded on the audit event). No "Check now": the
 * owner (review wxb07n, 2026-10-21) saw it as a way to spend tokens; the
 * sweep runs the check when it is due. Rows open the item's workspace,
 * which marks the answer read. The list is the shell's one 30s poll (Outlet context); this page
 * never starts its own.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { failedActionMessage } from '../../components/portal/investigationsView';
import { trackEvent } from '../../services/portalTracking';
import { cancelFollowup, type MerlinFollowupRead } from '../../services/merlinFollowups';
import type { DoorThreadRead } from '../../services/qcpInvestigations';
import { useQualityTriage } from './qualityTriageContext';
import { fromTab, resolveThread, threadWorkspacePath } from './qualityWorkspaceView';
import {
  askedByWord,
  dueWord,
  followupSeenSnapshot,
  groupPending,
  isOpenFollowup,
  isSettledUnseen,
  markFollowupSeen,
  settledLine,
  statusWord,
} from './pendingView';
import styles from './QualityTriagePages.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

/** The reason a cancel must carry (backend: 2..MAX_REASON_CHARS). */
const MIN_REASON_CHARS = 2;

function badgeClass(row: MerlinFollowupRead): string {
  if (row.status === 'checking') return styles.sev_high;
  if (row.status === 'fired' || row.status === 'satisfied') return styles.sev_answered;
  return styles.sev_watch;
}

function PendingRow({
  row,
  thread,
  viewerName,
  unseen,
  busy,
  confirming,
  error,
  onOpen,
  onAskCancel,
  onCancel,
  onDismissConfirm,
  onMarkRead,
}: {
  row: MerlinFollowupRead;
  /** The thread it hangs on, when the door's list has it. */
  thread: DoorThreadRead | null;
  viewerName: string | null;
  /** Settled by Merlin and not yet read here. */
  unseen: boolean;
  busy: boolean;
  /** The "cancel it?" step is open under this row. */
  confirming: boolean;
  /** Why the last action on THIS row failed, shown under it. */
  error: string | null;
  onOpen: (row: MerlinFollowupRead) => void;
  onAskCancel: (row: MerlinFollowupRead) => void;
  onCancel: (row: MerlinFollowupRead, reason: string) => void;
  onDismissConfirm: () => void;
  onMarkRead: (row: MerlinFollowupRead) => void;
}) {
  // A read-only viewer never withdraws the desk's follow-ups (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  const [reason, setReason] = useState('');
  const open = isOpenFollowup(row);
  const where = row.anchor_ref ?? (thread ? thread.thread_no : null);
  const due = dueWord(row);
  return (
    <div className={styles.rowGroup} data-testid="pending-row" data-status={row.status}>
      <div className={styles.rowWrap}>
        <button
          type="button"
          className={`${styles.row} ${!open && !unseen ? styles.rowDimmed : ''}`}
          onClick={() => onOpen(row)}
          data-door-item={where ?? row.id}
          data-action="qcp_pending_open"
          data-target-followup-id={row.id}
          title={thread ? `Open ${thread.thread_no} · ${thread.title}` : 'Open the chat it was promised in'}
        >
          <span className={`${styles.sev} ${badgeClass(row)}`}>{statusWord(row).toUpperCase()}</span>
          <span className={styles.rowBody}>
            <span className={styles.rowTitle}>{row.promise_text}</span>
            {row.condition_text && row.condition_text !== row.promise_text && (
              <span className={styles.rowSub}>{row.condition_text}</span>
            )}
            {!open && (
              <span className={unseen ? styles.respondedLine : styles.rowSub} data-testid="pending-settled">
                {unseen ? '✓ ' : ''}
                {settledLine(row)}
              </span>
            )}
            <span className={styles.chips}>
              {open && due && (
                <span className={`${styles.chip} ${styles.chipDue}`} data-testid="pending-due">
                  ⏰ {due}
                </span>
              )}
              {unseen && (
                <span className={styles.chipNew} data-testid="pending-new">
                  New
                </span>
              )}
              <span className={styles.chip} data-testid="pending-asked-by">
                {askedByWord(row, viewerName)}
              </span>
              {row.check_count > 0 && (
                <span className={`${styles.chip} ${styles.chipAge}`}>
                  checked {row.check_count} time{row.check_count === 1 ? '' : 's'}
                </span>
              )}
            </span>
          </span>
          <span className={styles.rowRight}>
            {where ? (
              <span title={thread?.title ?? undefined}>{where}</span>
            ) : (
              <span>chat</span>
            )}
          </span>
        </button>
        {open && !readOnly && (
          <button
            type="button"
            className={styles.rowAction}
            onClick={() => onAskCancel(row)}
            disabled={busy || confirming}
            title="Withdraw this follow-up — Merlin stops watching"
            data-testid="pending-cancel"
          >
            ✕ Cancel
          </button>
        )}
        {unseen && (
          <button
            type="button"
            className={styles.rowAction}
            onClick={() => onMarkRead(row)}
            title="Keep it, but stop counting it as new"
            data-testid="pending-mark-read"
          >
            ✓ Mark read
          </button>
        )}
      </div>
      {confirming && (
        <div
          className={styles.rowConfirm}
          role="group"
          aria-label="Cancel this follow-up?"
          data-testid="confirm-step"
        >
          <span className={styles.rowConfirmText}>
            Cancel this follow-up? Merlin stops watching{where ? ` ${where}` : ''} for it. Say why,
            for the record:
          </span>
          <input
            type="text"
            className={styles.rowConfirmInput}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. already resolved by phone"
            aria-label="Reason"
            maxLength={500}
            autoFocus
          />
          <button
            type="button"
            className={styles.rowAction}
            onClick={() => onCancel(row, reason.trim())}
            disabled={busy || reason.trim().length < MIN_REASON_CHARS}
            data-testid="confirm-yes"
          >
            {busy ? 'Cancelling…' : 'Cancel it'}
          </button>
          <button type="button" className={styles.rowConfirmNo} onClick={onDismissConfirm} disabled={busy}>
            Keep it
          </button>
        </div>
      )}
      {error && (
        <p className={styles.errorNote} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function Lane({
  title,
  sub,
  rows,
  empty,
  children,
}: {
  title: string;
  sub?: string;
  rows: MerlinFollowupRead[];
  empty?: string;
  children: (row: MerlinFollowupRead) => ReactNode;
}) {
  if (rows.length === 0 && !empty) return null;
  return (
    <>
      <h2 className={styles.lane}>
        {title}
        {rows.length > 0 && <span className={styles.lanePill}>{rows.length}</span>}
      </h2>
      {sub && <p className={styles.laneSub}>{sub}</p>}
      {rows.length === 0 && empty && <p className={styles.emptyNote}>{empty}</p>}
      {rows.map(children)}
    </>
  );
}

export default function QualityPendingPage() {
  const { invState, userName, followups, bumpSeen } = useQualityTriage();
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ id: string; message: string } | null>(null);
  // Re-read the seen marks after one is written (the groups move).
  const [seenTick, setSeenTick] = useState(0);
  const followupRows = followups?.rows;
  const rows = useMemo(() => followupRows ?? [], [followupRows]);
  const seen = useMemo(
    () => followupSeenSnapshot(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seenTick re-reads localStorage seen-marks
    [seenTick, rows],
  );
  const groups = useMemo(() => groupPending(rows, seen), [rows, seen]);
  const order = [...groups.firedUnseen, ...groups.dueThisWeek, ...groups.later, ...groups.settled];

  const threadFor = (row: MerlinFollowupRead): DoorThreadRead | null =>
    row.anchor_kind === 'signal_thread'
      ? (resolveThread(invState.threads, row.anchor_ref ?? undefined) ??
        resolveThread(invState.threads, row.anchor_id ?? undefined))
      : null;

  const seenNow = (row: MerlinFollowupRead) => {
    if (!isSettledUnseen(row, followupSeenSnapshot())) return;
    markFollowupSeen(row);
    setSeenTick((t) => t + 1);
    bumpSeen();
  };

  const openRow = (row: MerlinFollowupRead) => {
    const thread = threadFor(row);
    seenNow(row);
    trackEvent('click', 'qcp_pending_open', {
      followup_id: row.id,
      status: row.status,
      thread_no: thread?.thread_no ?? row.anchor_ref ?? null,
      outcome: thread ? 'thread' : row.conversation_id ? 'chat' : 'none',
    });
    if (thread) {
      navigate(
        threadWorkspacePath(thread, 'tab'),
        fromTab(
          'pending',
          order.map((r) => threadFor(r)?.thread_no).filter((n): n is string => !!n),
        ),
      );
      return;
    }
    if (row.conversation_id) {
      navigate(`../ask?conversation=${encodeURIComponent(row.conversation_id)}`);
    }
  };

  const markRead = (row: MerlinFollowupRead) => {
    trackEvent('action', 'qcp_pending_mark_read', { followup_id: row.id, status: row.status });
    seenNow(row);
  };

  const cancel = async (row: MerlinFollowupRead, reason: string) => {
    if (busyId) return;
    setBusyId(row.id);
    setFailure(null);
    try {
      const updated = await cancelFollowup(row.id, reason);
      trackEvent('action', 'qcp_pending_cancel', { followup_id: row.id, outcome: 'ok' });
      followups?.applyUpdate(updated);
      setConfirmingId(null);
    } catch (e) {
      trackEvent('action', 'qcp_pending_cancel', { followup_id: row.id, outcome: 'failed' });
      setFailure({ id: row.id, message: failedActionMessage('cancel this follow-up', e) });
    } finally {
      setBusyId(null);
    }
  };


  const renderRow = (row: MerlinFollowupRead) => (
    <PendingRow
      key={row.id}
      row={row}
      thread={threadFor(row)}
      viewerName={userName}
      unseen={isSettledUnseen(row, seen)}
      busy={busyId === row.id}
      confirming={confirmingId === row.id}
      error={failure?.id === row.id ? failure.message : null}
      onOpen={openRow}
      onAskCancel={(r) => {
        setFailure(null);
        setConfirmingId(r.id);
      }}
      onCancel={(r, reason) => void cancel(r, reason)}
      onDismissConfirm={() => setConfirmingId(null)}
      onMarkRead={markRead}
    />
  );

  if (followups && followups.available === false) {
    return (
      <div className={styles.page}>
        <p className={styles.emptyNote} data-testid="pending-unavailable">
          Follow-ups are not switched on for this deploy.
        </p>
      </div>
    );
  }

  const open = groups.dueThisWeek.length + groups.later.length;

  return (
    <div className={styles.page}>
      <p className={styles.legend}>
        Everything you asked Merlin to watch or come back on, and what it promised on its own.
        When a check comes due Merlin writes what it found on the item and rings the bell; the
        answer waits here until you read it. Cancel one to stop the watch.
      </p>

      {followups?.loaded && rows.length === 0 && (
        <p className={styles.emptyNote} data-testid="pending-empty">
          Nothing pending. When Merlin promises to check on something in a chat, it shows up here.
        </p>
      )}

      <Lane
        title="Answered · not yet read"
        sub="Merlin came back on these. Open one to read what it found on the item."
        rows={groups.firedUnseen}
      >
        {renderRow}
      </Lane>
      <Lane
        title="Due this week"
        sub={open > 0 ? 'What Merlin checks in the next seven days, soonest first; a watch with no date is checked daily.' : undefined}
        rows={groups.dueThisWeek}
        empty={rows.length > 0 ? 'Nothing due this week.' : undefined}
      >
        {renderRow}
      </Lane>
      <Lane title="Later" rows={groups.later}>
        {renderRow}
      </Lane>
      <Lane
        title="Settled"
        sub="Read, cancelled, or the window closed."
        rows={groups.settled}
      >
        {renderRow}
      </Lane>
    </div>
  );
}
